import { useParams } from 'react-router-dom';

/**
 * Custom hook to decode specific URL parameters.
 *
 * This hook uses `useParams` to retrieve URL parameters and decodes the value of the `productId` parameter using `decodeURIComponent`. Other parameters are returned as-is.
 * The parameter currently referred to as productId is actually the user-defined product name which can contain special characters that break the routing, e.g. slashes.
 *
 * See https://samsonvt.atlassian.net/browse/MAG-3439 for background into the current need for this.
 *
 * @template T - A record type representing the URL parameters.
 * @returns {T} - An object containing the decoded URL parameters.
 * @example
 * const { productId } = useDecodedParams<{ productId: string }>();
 */
export function useDecodedParams<Params extends { [K in keyof Params]?: string } = {}>(): Params {
  const params = useParams<Params>();

  const decodedParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      key === 'productId' && typeof value === 'string' ? decodeURIComponent(value) : value,
    ])
  ) as Params;

  return decodedParams;
}
